/** @format */

import {useState} from 'react';

import {Alert, Box, LinearProgress} from '@mui/material';
import {Trans} from 'react-i18next';

import {OauthProvider} from 'graphql/generated/types';
import {SegmentedRadioButton} from 'components/buttons/SegmentedGroup/SegmentedRadioButton';
import {SegmentedBox} from 'components/buttons/SegmentedGroup/SegmentedBox';
import {useMixpanelContext} from 'contexts/MixpanelContext';

import PurchaseAiReportsModal from 'components/PurchaseAiReportsModal';

import {
  AiModuleContainer,
  HeaderContainer,
  ModuleTitle,
  ContentContainer,
  BetaChip,
  MonitoringAlertBadge,
} from './styles';
import Form from './components/Form';
import ReportsStub from './components/ReportsStub';
import BriefReports from './components/BriefReports';
import MonitoringTab from './components/MonitoringTab';
import {useAiModuleData} from './hooks/useAiModuleData';

type TabValue = 'monitoring' | 'research';

interface AiModuleProps {
  platforms: OauthProvider[];
}

const AiModule = ({platforms}: AiModuleProps) => {
  const [activeTab, setActiveTab] = useState<TabValue>('monitoring');

  const {
    loading,
    aiReportsLoading,
    reportsLimitLoading,
    generateReportLoading,
    pricingLoading,
    error,
    aiReportsError,
    reportsLimitError,
    formDataOptions,
    manualReports,
    monitoringReports,
    remainingReports,
    purchaseState,
    unitPrice,
    handleFormSubmit,
    refetchData,
    needToConnectMetrika,
    monitoringConfig,
  } = useAiModuleData({platforms});

  const {mixpanel} = useMixpanelContext();
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);

  const handleOpenPurchaseModal = () => {
    if (purchaseState === 'has_balance') return;
    mixpanel.track('ai_reports_purchase_modal_open', {
      from_state: purchaseState,
    });
    setPurchaseModalOpen(true);
  };

  const handleClosePurchaseModal = () => setPurchaseModalOpen(false);

  const purchaseModal = (
    <PurchaseAiReportsModal
      open={purchaseModalOpen}
      onClose={handleClosePurchaseModal}
      unitPrice={unitPrice}
    />
  );

  if ((error || reportsLimitError || aiReportsError) && !needToConnectMetrika) {
    return (
      <Alert severity='error'>
        {error?.message || reportsLimitError?.message || aiReportsError?.message}
      </Alert>
    );
  }

  if (loading || reportsLimitLoading || aiReportsLoading) {
    return <LinearProgress style={{flex: 1, marginTop: 16}} />;
  }

  const isMonitoringActive = monitoringConfig?.status === 'ACTIVE';
  const researchReports = manualReports;

  return (
    <AiModuleContainer>
      <HeaderContainer>
        <ModuleTitle>ИИ-модуль</ModuleTitle>
        <BetaChip label='Бета' />
        {!isMonitoringActive && (
          <MonitoringAlertBadge>
            <Box component='span' className='material-symbols-outlined' style={{fontSize: 14}}>
              vital_signs
            </Box>
            <Trans>Бесплатный ИИ-мониторинг не запущен</Trans>
          </MonitoringAlertBadge>
        )}
      </HeaderContainer>

      <SegmentedBox sx={{borderRadius: '8px 8px 0 0'}}>
        <SegmentedRadioButton
          active={activeTab === 'monitoring'}
          onClick={() => setActiveTab('monitoring')}
          size='medium'
          activeBgColor='linear-gradient(180deg, #316E9F 0%, #3E88C5 100%)'
        >
          <Trans>Ежемесячный мониторинг</Trans>
        </SegmentedRadioButton>
        <SegmentedRadioButton
          active={activeTab === 'research'}
          onClick={() => setActiveTab('research')}
          size='medium'
          activeBgColor='linear-gradient(180deg, #316E9F 0%, #3E88C5 100%)'
        >
          <Trans>ИИ-исследование</Trans>
        </SegmentedRadioButton>
      </SegmentedBox>

      {activeTab === 'monitoring' ? (
        <MonitoringTab
          formDataOptions={formDataOptions}
          monitoringConfig={monitoringConfig}
          monitoringReports={monitoringReports}
          refetchData={refetchData}
          needToConnectMetrika={needToConnectMetrika}
        />
      ) : (
        <ContentContainer sx={{'& > *:first-of-type': {borderTopLeftRadius: 0}}}>
          <Form
            onSubmit={handleFormSubmit}
            formDataOptions={formDataOptions}
            purchaseState={purchaseState}
            remainingReports={remainingReports}
            onOpenPurchaseModal={handleOpenPurchaseModal}
            pricingLoading={pricingLoading}
            unitPrice={unitPrice}
            generateReportLoading={generateReportLoading}
            needToConnectMetrika={needToConnectMetrika}
          />
          {researchReports.length > 0 ? <BriefReports data={researchReports} /> : <ReportsStub />}
        </ContentContainer>
      )}
      {purchaseModal}
    </AiModuleContainer>
  );
};

export default AiModule;
